/**
 * Vorschlagssuche für Produkt- und Versionsauswahl (Befund B3): Mantines
 * eingebauter Autocomplete-Filter verlangt die getippte Eingabe als
 * zusammenhängenden Teilstring des Namens. Wer „sap ui5" tippt, findet damit
 * „UI5" nicht mehr — der Suchstring „sap ui5" steckt nirgends als Ganzes in
 * „UI5". Ergebnis: Der Tester legte ein zweites Produkt „SAP UI5" an, obwohl
 * „UI5" längst im Katalog stand.
 *
 * Diese Suche zerlegt die Eingabe stattdessen in Wörter und schlägt jeden
 * Eintrag vor, in dem mindestens eines der Wörter vorkommt — an beliebiger
 * Stelle, nicht nur am Anfang. Genauere Treffer (Eintrag beginnt mit der
 * kompletten Eingabe) stehen vor Treffern, die die Eingabe nur enthalten, die
 * wiederum vor reinen Wort-Treffern stehen.
 */

/**
 * Vergleichsform wie serverseitig (`apps/api/.../maintenance/versionLine.ts::normalizeLabel`):
 * klein, Leerraum zusammengefasst. Muss mit dem Server exakt gleichbleiben,
 * sonst weichen Anlegen-Erkennung hier und Dubletten-Erkennung dort wieder
 * auseinander — es gibt keinen gemeinsamen Ort für diese eine Zeile, weil
 * Frontend und API-Modul unterschiedliche Packages sind.
 */
export function normalizeLabel(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, " ");
}

export interface FilterableOption {
  value: string;
  label: string;
}

/**
 * Rang eines Treffers — je kleiner, desto weiter oben in der Liste:
 * 0 = Name beginnt mit der kompletten Eingabe (genauester Treffer)
 * 1 = Name enthält die komplette Eingabe (an beliebiger Stelle)
 * 2 = mindestens ein Wort der Eingabe kommt im Namen vor
 */
function matchRank(name: string, normalizedQuery: string, queryWords: string[]): number | null {
  if (name.startsWith(normalizedQuery)) return 0;
  if (name.includes(normalizedQuery)) return 1;
  if (queryWords.some((word) => name.includes(word))) return 2;
  return null;
}

/**
 * Filtert und sortiert `options` nach Übereinstimmung mit `query` — wortweise,
 * Treffer an beliebiger Stelle im Namen, genauere Treffer zuerst. Reine
 * Funktion, damit die Suchlogik ohne Browser/Mantine testbar ist; wird über
 * Autocompletes `filter`-Prop eingehängt (ersetzt Mantines Default-Filter,
 * der nur den gesamten Suchstring als zusammenhängenden Teilstring sucht).
 */
export function filterSoftwareOptions<T extends FilterableOption>(
  options: readonly T[],
  query: string,
): T[] {
  const normalizedQuery = normalizeLabel(query);
  if (normalizedQuery.length === 0) {
    return [...options];
  }
  const queryWords = normalizedQuery.split(" ").filter((w) => w.length > 0);

  const ranked: { option: T; rank: number }[] = [];
  for (const option of options) {
    const rank = matchRank(normalizeLabel(option.label), normalizedQuery, queryWords);
    if (rank !== null) {
      ranked.push({ option, rank });
    }
  }
  // Array.prototype.sort ist stabil (ES2019+) — Einträge mit demselben Rang
  // behalten ihre bisherige Reihenfolge (z. B. Versionen weiterhin nach
  // Systemanzahl absteigend, wie vom Aufrufer vorsortiert).
  ranked.sort((a, b) => a.rank - b.rank);
  return ranked.map((r) => r.option);
}
